import React, { useState } from 'react';
import { useShortcodes } from '@/hooks/useShortcodes';
import { Info, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ShortcodeHelperProps {
  isVisible?: boolean;
  className?: string;
  isGlobalDarkMode?: boolean;
}

export const ShortcodeHelper: React.FC<ShortcodeHelperProps> = ({ 
  isVisible = true, 
  className = '',
  isGlobalDarkMode = false
}) => {
  const { availableShortcodes, getBrandData, brandData } = useShortcodes();
  const [copiedShortcode, setCopiedShortcode] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  if (!isVisible || !brandData) return null;

  const brandDataValues = getBrandData();

  const handleCopyShortcode = async (shortcode: string) => {
    try {
      await navigator.clipboard.writeText(shortcode);
      setCopiedShortcode(shortcode);
      setTimeout(() => setCopiedShortcode(null), 2000);
    } catch (err) {
      console.error('Failed to copy shortcode:', err);
    }
  };

  return (
    <TooltipProvider>
      <div className={`fixed bottom-4 right-4 z-50 ${className}`}>
        <Tooltip open={isOpen} onOpenChange={setIsOpen}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              className="backdrop-blur-sm shadow-lg transition-colors bg-card/90 text-foreground hover:bg-accent border border-border"
            >
              <Info className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="w-72 p-0 border-0">
            <div className="pt-3 pb-2 px-2 rounded-lg shadow-xl bg-card border border-border">
              <h3 className="font-semibold mb-1 flex items-center text-foreground">
                <Info className="w-4 h-4 mr-2 text-workspace-accent" />
                Available Shortcodes
              </h3>
              <p className="text-sm mb-2 text-muted-foreground">
                Use these shortcodes in your content to automatically fetch brand data:
              </p>
              
              <div className="space-y-1">
                {availableShortcodes.map((shortcode) => {
                  const isCopied = copiedShortcode === shortcode;
                  
                  return (
                    <div key={shortcode} className="flex items-center justify-between p-1.5 rounded bg-muted/50">
                      <div className="flex-1">
                        <div className="font-mono text-sm text-workspace-accent">{shortcode}</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyShortcode(shortcode)}
                        className="ml-2 h-7 w-7 p-0 hover:bg-accent"
                      >
                        {isCopied ? (
                          <Check className="w-3.5 h-3.5 text-green-500" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-2 p-1.5 rounded bg-workspace-accent-light">
                <p className="text-xs text-foreground">
                  <strong>Tip:</strong> Copy a shortcode and paste it into any editable text field. 
                  It will automatically be replaced with the actual brand data when saved.
                </p>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};
